import React from 'react'
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  MessageCircle,
  Send,
  Music2
} from 'lucide-react'

/**
 * Social Media Links Component
 * Displays all available social media links with icons and animations
 * Only shows non-empty links and skips "#" values
 */

const SOCIAL_CONFIG = {
  facebook: {
    icon: Facebook,
    color: 'hover:text-blue-600',
    bgColor: 'hover:bg-blue-100',
    label: 'Facebook'
  },
  twitter: {
    icon: Twitter,
    color: 'hover:text-blue-400',
    bgColor: 'hover:bg-blue-100',
    label: 'Twitter/X'
  },
  instagram: {
    icon: Instagram,
    color: 'hover:text-pink-600',
    bgColor: 'hover:bg-pink-100',
    label: 'Instagram'
  },
  tiktok: {
    icon: Music2,
    color: 'hover:text-gray-900',
    bgColor: 'hover:bg-gray-200',
    label: 'TikTok'
  },
  whatsapp: {
    icon: MessageCircle,
    color: 'hover:text-green-600',
    bgColor: 'hover:bg-green-100',
    label: 'WhatsApp'
  },
  telegram: {
    icon: Send,
    color: 'hover:text-blue-500',
    bgColor: 'hover:bg-blue-100',
    label: 'Telegram'
  }
}

export function SocialMediaLinks({ 
  socialLinks = {}, 
  size = 'md',
  variant = 'icon-only',
  theme = 'light'
}) {
  // Filter out empty links and "#" symbols
  const validLinks = Object.entries(socialLinks).filter(([key, url]) => {
    return url && url.trim() !== '' && url.trim() !== '#'
  })

  if (validLinks.length === 0) {
    return null
  }

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  }

  const isDark = theme === 'dark'

  return (
    <div className="social-media-links">
      {/* Desktop/Tablet View */}
      <div className="hidden sm:flex items-center gap-3">
        {validLinks.map(([key, url]) => {
          const config = SOCIAL_CONFIG[key]
          if (!config) return null
          const Icon = config.icon

          return (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={config.label}
              className={`
                flex items-center justify-center
                ${sizeClasses[size]}
                transition-all duration-300 transform
                ${isDark 
                  ? 'bg-gray-700 text-white' 
                  : 'bg-gray-100 text-gray-600'
                }
                rounded-lg
                hover:scale-110
                ${isDark ? 'hover:bg-gray-600' : config.bgColor}
                ${isDark ? 'hover:text-white' : config.color}
              `}
            >
              <Icon className="w-full h-full p-2" strokeWidth={1.5} />
            </a>
          )
        })}
      </div>

      {/* Mobile View - Horizontal scroll */}
      <div className="sm:hidden overflow-x-auto pb-2">
        <div className="flex items-center gap-2 w-max">
          {validLinks.map(([key, url]) => {
            const config = SOCIAL_CONFIG[key]
            if (!config) return null
            const Icon = config.icon

            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={config.label}
                className={`
                  flex items-center justify-center
                  w-9 h-9
                  transition-all duration-300 transform
                  ${isDark 
                    ? 'bg-gray-700 text-white' 
                    : 'bg-gray-100 text-gray-600'
                  }
                  rounded-lg
                  active:scale-95
                  ${isDark ? 'hover:bg-gray-600' : config.bgColor}
                  ${isDark ? 'hover:text-white' : config.color}
                `}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </a>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default SocialMediaLinks
